import { getFetchRequest, postFetchRequest, deleteFetchRequest, MAKE_ORDER_REQUEST } from '../store/requests';
import { makeUrl } from '../const/const';

class CartService {
    constructor() {
        this.currentCart = null;
        this.observers = [];
    }

    // Подписка на изменения корзины
    subscribe(observer) {
        this.observers.push(observer);
    }

    // Отписка от изменений корзины
    unsubscribe(observer) {
        this.observers = this.observers.filter(obs => obs !== observer);
    }

    // Уведомление подписчиков об изменениях
    notify() {
        this.observers.forEach(observer => observer(this.currentCart));
    }

    // Получить текущую корзину (заказ со статусом CREATED)
    async getCurrentCart() {
        return new Promise((resolve, reject) => {
            const url = makeUrl([MAKE_ORDER_REQUEST]) + '?status=CREATED';
            
            getFetchRequest(
                url,
                (orders) => {
                    console.log(`🛒 [CartService] Raw orders from server:`, orders);
                    if (orders && orders.length > 0) {
                        this.currentCart = orders[0];
                        console.log(`🛒 [CartService] Selected cart:`, this.currentCart);
                        if (this.currentCart && this.currentCart.products) {
                            console.log(`🛒 [CartService] Cart has ${this.currentCart.products.length} products:`);
                            this.currentCart.products.forEach((product, index) => {
                                console.log(`🛒 [CartService Product ${index}] Server response:`, {
                                    productId: product.productId,
                                    productName: product.productName,
                                    productPriceId: product.productPriceId,
                                    // 🔧 НОВЫЕ ПОЛЯ для расчета цен в заказе
                                    distributorId: product.distributorId,
                                    packerId: product.packerId,
                                    manufacturerName: product.manufacturerName,
                                    stockId: product.stockId,
                                    duty: product.duty,
                                    allServerFields: Object.keys(product),
                                    fullServerProduct: product,
                                });
                            });
                        }
                    } else {
                        this.currentCart = null;
                        console.log(`🛒 [CartService] No cart found from server`);
                    }
                    this.notify();
                    resolve(this.currentCart);
                },
                (error) => {
                    console.error('❌ Ошибка получения корзины:', error);
                    reject(error);
                },
            );
        });
    }

    // 🔇 ТИХОЕ получение корзины (БЕЗ уведомления подписчиков)
    async getCurrentCartQuiet() {
        return new Promise((resolve, reject) => {
            const url = makeUrl([MAKE_ORDER_REQUEST]) + '?status=CREATED';
            
            getFetchRequest(
                url,
                (orders) => {
                    console.log(`🔇 [CartService Quiet] Raw orders from server:`, orders);
                    let cartFromServer = null;
                    if (orders && orders.length > 0) {
                        cartFromServer = orders[0];
                        console.log(`🔇 [CartService Quiet] Server cart has ${cartFromServer?.products?.length || 0} products`);
                    
                        // 🔧 Логируем структуру товаров из тихого запроса  
                        if (cartFromServer && cartFromServer.products) {
                            cartFromServer.products.forEach((product, index) => {
                                console.log(`🔇 [CartService Quiet Product ${index}]:`, {
                                    productName: product.productName,
                                    distributorId: product.distributorId,
                                    packerId: product.packerId,
                                    manufacturerName: product.manufacturerName,
                                    stockId: product.stockId,
                                });
                            });
                        }
                    } else {
                        console.log(`🔇 [CartService Quiet] No cart found from server`);
                    }
                    // 🚀 БЕЗ ОБНОВЛЕНИЯ this.currentCart и БЕЗ this.notify()
                    resolve(cartFromServer);
                },
                (error) => {
                    console.error('❌ Ошибка получения корзины (quiet):', error);
                    reject(error);
                },
            );
        });
    }

    // Создать новую корзину (заказ) с повторными попытками
    async createCart(retryCount = 0) {
        const maxRetries = 2;
        
        return new Promise((resolve, reject) => {
            const url = makeUrl([MAKE_ORDER_REQUEST]);
            console.log(`🛒 [CartService] Creating new cart with URL: ${url} (attempt ${retryCount + 1})`);
            
            postFetchRequest(
                url,
                {},
                async (response) => {
                    console.log(`🛒 [CartService] Server response for new cart:`, response);
                    
                    // Если response - это объект Response (а не JSON), обрабатываем его
                    let newOrder;
                    if (response && typeof response.json === 'function') {
                        // Это объект Response
                        if (!response.ok) {
                            console.error(`❌ [CartService] Server returned error status: ${response.status}`);
                            const errorText = await response.text().catch(() => 'Unknown error');
                            reject(new Error(`Server error ${response.status}: ${errorText}`));
                            return;
                        }
                        
                        try {
                            // Проверяем есть ли тело ответа
                            const responseText = await response.text();
                            console.log(`🔍 [CartService] Response text:`, responseText);
                            
                            if (!responseText || responseText.trim() === '') {
                                console.error(`❌ [CartService] Empty response body`);
                                reject(new Error('Server returned empty response'));
                                return;
                            }
                            
                            try {
                                newOrder = JSON.parse(responseText);
                            } catch (parseError) {
                                console.error(`❌ [CartService] JSON parse error:`, parseError);
                                console.error(`❌ [CartService] Response text that failed to parse:`, responseText);
                                reject(new Error(`Invalid JSON response: ${parseError.message}`));
                                return;
                            }
                        } catch (e) {
                            console.error(`❌ [CartService] Failed to read response:`, e);
                            reject(new Error('Failed to read server response'));
                            return;
                        }
                    } else {
                        // Это уже JSON объект
                        newOrder = response;
                    }
                    
                    console.log(`🛒 [CartService] Parsed cart data:`, newOrder);
                    console.log(`🛒 [CartService] New cart ID: ${newOrder?.id}, type: ${typeof newOrder?.id}`);
                    
                    // 🔧 ДЕТАЛЬНОЕ ЛОГИРОВАНИЕ для отладки
                    console.log(`🔍 [CartService DEBUG] newOrder analysis:`, {
                        newOrder,
                        isNull: newOrder === null,
                        isUndefined: newOrder === undefined,
                        isFalsy: !newOrder,
                        hasId: !!newOrder?.id,
                        idValue: newOrder?.id,
                        idType: typeof newOrder?.id,
                        objectKeys: newOrder ? Object.keys(newOrder) : 'N/A',
                        jsonString: JSON.stringify(newOrder),
                    });
                    
                    // 🔧 КРИТИЧЕСКИ ВАЖНАЯ ПРОВЕРКА!
                    if (!newOrder || !newOrder.id) {
                        console.error(`❌ [CartService] CRITICAL ERROR: Server returned invalid cart:`, {
                            newOrder,
                            id: newOrder?.id,
                            typeof_id: typeof newOrder?.id,
                            keys: Object.keys(newOrder || {}),
                            retryCount,
                            maxRetries,
                        });
                        
                        // Если это не последняя попытка, пробуем еще раз через небольшую задержку
                        if (retryCount < maxRetries) {
                            console.log(`🔄 [CartService] Retrying cart creation in 1 second... (${retryCount + 1}/${maxRetries})`);
                            setTimeout(() => {
                                this.createCart(retryCount + 1).then(resolve).catch(reject);
                            }, 1000);
                            return;
                        }
                        
                        reject(new Error(`Сервер вернул некорректную корзину: ${JSON.stringify(newOrder)}`));
                        return;
                    }
                    
                    this.currentCart = newOrder;
                    this.notify();
                    resolve(newOrder);
                },
                async (error) => {
                    console.error('❌ Ошибка создания корзины:', error);
                    
                    // Если error - это Response объект с ошибкой
                    if (error && typeof error.json === 'function') {
                        try {
                            const errorText = await error.text().catch(() => 'Unknown error');
                            console.error(`❌ Server error ${error.status}: ${errorText}`);
                            error = new Error(`Server error ${error.status}: ${errorText}`);
                        } catch (e) {
                            console.error(`❌ Failed to parse error response:`, e);
                        }
                    }
                    
                    // Если это не последняя попытка, пробуем еще раз
                    if (retryCount < maxRetries) {
                        console.log(`🔄 [CartService] Retrying cart creation after error in 1 second... (${retryCount + 1}/${maxRetries})`);
                        setTimeout(() => {
                            this.createCart(retryCount + 1).then(resolve).catch(reject);
                        }, 1000);
                        return;
                    }
                    
                    reject(error);
                },
                false, // Ожидаем JSON ответ при успехе
            );
        });
    }

    // Получить или создать корзину
    async getOrCreateCart() {
        try {
            await this.getCurrentCart();
            if (!this.currentCart) {
                await this.createCart();
            }
            return this.currentCart;
        } catch (error) {
            console.error('Ошибка получения/создания корзины:', error);
            throw error;
        }
    }

    // Добавить товар в корзину
    async addProductToCart(productData) {
        try {
            console.log(`🛒 [CartService] Starting addProductToCart for product:`, productData);
            
            // Сначала проверяем есть ли уже корзина
            await this.getCurrentCart();
            console.log(`🛒 [CartService] Current cart after getCurrentCart():`, this.currentCart);
            
            let cartToUse = this.currentCart;
            
            // Если корзины нет, создаем новую
            if (!cartToUse) {
                console.log(`🛒 [CartService] No existing cart found, creating new one...`);
                cartToUse = await this.createCart();
                console.log(`🛒 [CartService] New cart created:`, cartToUse);
                
                // Дополнительная проверка: убеждаемся что корзина сохранилась в this.currentCart
                if (this.currentCart && this.currentCart.id) {
                    console.log(`🛒 [CartService] Confirmed cart is stored in this.currentCart:`, this.currentCart.id);
                    cartToUse = this.currentCart;
                } else {
                    console.warn(`⚠️ [CartService] Cart created but not stored in this.currentCart, using returned value`);
                }
            }
            
            // 🔧 КРИТИЧЕСКИ ВАЖНАЯ ПРОВЕРКА!
            if (!cartToUse || !cartToUse.id) {
                console.error(`❌ [CartService] CRITICAL ERROR: cartToUse is invalid:`, {
                    cartToUse,
                    id: cartToUse?.id,
                    typeof_id: typeof cartToUse?.id,
                });
                throw new Error(`Корзина не создана или отсутствует ID. cartToUse: ${JSON.stringify(cartToUse)}`);
            }
            
            console.log(`🛒 [CartService] Using cart with ID: ${cartToUse.id}`);
            
            // Подготавливаем данные для добавления товара
            const requestData = {
                productId: productData.productId,
                productCount: productData.productCount,
                productPriceId: productData.productPriceId,
            };
            


            return new Promise((resolve, reject) => {
                // 🔧 Логируем формирование URL 
                const urlParts = [MAKE_ORDER_REQUEST, cartToUse.id, '/product/'];
                console.log(`🛒 [CartService] Building URL with parts:`, urlParts);
                const finalUrl = makeUrl(urlParts);
                console.log(`🛒 [CartService] Final URL: ${finalUrl}`);
                
                postFetchRequest(
                    finalUrl,
                    requestData,
                    async (response) => {
                        console.log(`🛒 [CartService] Add product response:`, response);
                        
                        // Проверяем тип ответа - JSON или Response объект
                        if (response && typeof response.ok !== 'undefined') {
                            // Response объект (ошибка)
                            let errorText;
                            try {
                                errorText = await response.text();
                            } catch (e) {
                                errorText = 'Unknown error';
                            }
                            console.error('❌ Ошибка добавления в корзину:', {
                                status: response.status,
                                url: response.url,
                                finalUrl,
                                cartId: cartToUse.id,
                                error: errorText,
                            });
                            reject(new Error(`HTTP ${response.status}: ${errorText}`));
                        } else {
                            // JSON ответ (успех)
                            console.log(`✅ [CartService] Product added successfully:`, response);
                            // Обновляем корзину после добавления
                            await this.getCurrentCart();
                            resolve();
                        }
                    },
                    (error) => {
                        console.error('❌ Ошибка запроса добавления в корзину:', error);
                        reject(error);
                    },
                    false, // Теперь бэкенд возвращает JSON, используем автоматический парсинг
                );
            });
        } catch (error) {
            console.error('❌ Критическая ошибка добавления товара:', error);
            throw error;
        }
    }

    // Изменить количество товара в корзине
    async updateProductQuantity(productId, stockId, newQuantity) {
        if (!this.currentCart) {
            throw new Error('Корзина не найдена');
        }

        const data = {
            stockId: stockId,
            productCount: newQuantity,
        };

        return new Promise((resolve, reject) => {
            postFetchRequest(
                makeUrl([MAKE_ORDER_REQUEST, this.currentCart.id, '/product/', productId, '/']),
                data,
                async () => {
                    // Обновляем корзину после изменения
                    await this.getCurrentCart();
                    resolve();
                },
                (error) => {
                    console.error('Ошибка изменения количества:', error);
                    reject(error);
                },
                true,
            );
        });
    }

    // 🔧 ТИХОЕ изменение количества товара (БЕЗ перезагрузки корзины)
    async updateProductQuantityQuiet(productId, stockId, newQuantity) {
        if (!this.currentCart) {
            throw new Error('Корзина не найдена');
        }

        const data = {
            stockId: stockId,
            productCount: newQuantity,
        };

        return new Promise((resolve, reject) => {
            postFetchRequest(
                makeUrl([MAKE_ORDER_REQUEST, this.currentCart.id, '/product/', productId, '/']),
                data,
                () => {
                    // 🚀 БЕЗ ПЕРЕЗАГРУЗКИ КОРЗИНЫ - только API запрос
                    console.log(`🔇 [Quiet Update] Product ${productId} quantity updated to ${newQuantity}`);
                    resolve();
                },
                (error) => {
                    console.error('Ошибка изменения количества:', error);
                    reject(error);
                },
                true,
            );
        });
    }

    // Удалить товар из корзины
    async removeProductFromCart(productId, stockId, extra=null) {
        return this.removeProductFromCartQuiet(productId, stockId, extra);
    }

    // Удалить товар из корзины (без обновления UI)
    async removeProductFromCartQuiet(productId, stockId, extra=null) {
        if (!this.currentCart) {
            throw new Error('Корзина не найдена');
        }

        const url = makeUrl([MAKE_ORDER_REQUEST, this.currentCart.id, '/product/', productId, '/']);
        const data = {
            stockId: stockId,
            productPriceId: extra?.productPriceId ?? null,
            distributorId: extra?.distributorId ?? null,
            packerId: extra?.packerId ?? null,
            manufacturerName: extra?.manufacturerName ?? null,
        };

        return new Promise((resolve, reject) => {
            deleteFetchRequest(
                url,
                data,
                async (response) => {
                    if (response.ok) {
                        console.log(`✅ [DELETE] Product ${productId} (stockId: ${stockId}) removed from cart`);
                        resolve();
                    } else {
                        const errorText = await response.text();
                        console.error(`❌ [DELETE ERROR] ${response.status}: ${errorText}`);
                        reject(new Error(`HTTP ${response.status}: ${errorText}`));
                    }
                },
                (error) => {
                    console.error(`❌ [DELETE FAILED] ${productId}:`, error);
                    reject(error);
                },
                true, // withoutJSON = true
            );
        });
    }

    // Оформить заказ (изменить статус на PLACED)
    async submitOrder() {
        if (!this.currentCart) {
            throw new Error('Корзина не найдена');
        }

        const data = { status: 'PLACED' };

        return new Promise((resolve, reject) => {
            postFetchRequest(
                makeUrl([MAKE_ORDER_REQUEST, this.currentCart.id, '/status/']),
                data,
                () => {
                    // Корзина больше не активна
                    this.currentCart = null;
                    this.notify();
                    resolve();
                },
                (error) => {
                    console.error('Ошибка оформления заказа:', error);
                    reject(error);
                },
                true,
            );
        });
    }

    // Получить количество товаров в корзине
    getItemsCount() {
        if (!this.currentCart || !this.currentCart.products) {
            return 0;
        }
        return this.currentCart.products.reduce((total, product) => total + product.productCount, 0);
    }

    // Получить общую стоимость корзины
    getTotalCost() {
        return this.currentCart ? parseFloat(this.currentCart.totalCost || 0) : 0;
    }
}

// Экспортируем единственный экземпляр
export const cartService = new CartService();